import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { components, GET, paths } from './clients';

type LeaderboardListDto = components['schemas']['LeaderboardListDto'];
type LeaderboardListItemDto = components['schemas']['LeaderboardListItemDto'];

const QUERY_KEYS = {
  tickets: () => ['MY_TICKETS'],
  baseAchievements: (userId: string) => ['USER_ACHIEVEMENTS', userId],
  achievementsList: (userId: string) => QUERY_KEYS.baseAchievements(userId).concat('LIST'),
  certificate: (userId: string, achId: string) =>
    QUERY_KEYS.baseAchievements(userId).concat('ACHIEVEMENT', achId),
  leaderboard: (leaderboardId: string, limit: number, query?: string) => [
    'LEADERBOARD',
    leaderboardId,
    limit,
    query,
  ],
};

export function useMyTicketsQuery({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) {
  return useQuery({
    enabled,
    queryKey: QUERY_KEYS.tickets(),
    queryFn: async () => (await GET('/api/public/users/me/tickets')).data!,
  });
}

export function useAchievementsListQuery({
  userId,
  enabled = true,
}: {
  userId: string;
  enabled?: boolean;
}) {
  return useQuery({
    enabled,
    queryKey: QUERY_KEYS.achievementsList(userId),
    queryFn: async () =>
      (
        await GET('/api/public/users/{userId}/achievements/list', {
          params: { path: { userId } },
        })
      ).data!,
  });
}

export function useCertificateQuery({
  userId,
  achievementId,
  enabled = true,
}: {
  userId: string;
  achievementId: string;
  enabled?: boolean;
}) {
  return useQuery({
    enabled,
    queryKey: QUERY_KEYS.certificate(userId, achievementId),
    queryFn: async () =>
      (
        await GET(
          '/api/public/users/{userId}/achievements/{achievementId}/tournament-certificate',
          {
            params: { path: { userId, achievementId } },
          },
        )
      ).data!,
  });
}

export function createCertificatePdfLink({
  userId,
  achievementId,
}: {
  userId: string;
  achievementId: string;
}) {
  const USER = '{userId}';
  const ACHIEVEMENT = '{achievementId}';
  const path: keyof paths = `/api/public/users/${USER}/achievements/${ACHIEVEMENT}/tournament-certificate/pdf`;

  return `${__RUNTIME.userApi}${path.replace(USER, userId).replace(ACHIEVEMENT, achievementId)}`;
}

export function useInfiniteLeaderboardQuery(
  leaderboardId: string,
  {
    limit,
    query,
  }: {
    limit: number;
    query?: string;
  },
) {
  const result = useInfiniteQuery({
    initialPageParam: 0,
    getNextPageParam: (lastPage: LeaderboardListDto, _allPages, lastPageParam: number) => {
      if (lastPage.items.length < limit) return;
      return lastPageParam + 1;
    },
    queryKey: QUERY_KEYS.leaderboard(leaderboardId, limit, query),
    queryFn: async ({ pageParam }) =>
      (
        await GET('/api/public/leaderboards/{leaderboardId}/list', {
          params: {
            path: { leaderboardId },
            query: {
              limit,
              offset: pageParam * limit,
              query,
            },
          },
        })
      ).data!,
  });

  // Собираем все страницы в кучу
  let data: LeaderboardListItemDto[] = [];
  result?.data?.pages?.forEach((page) => {
    data = data.concat((page as { items: LeaderboardListItemDto[] }).items);
  });

  return { ...result, data };
}
